import { Box, Typography, useTheme } from '@mui/material';
import React from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    ResponsiveContainer,
    Tooltip,
    TooltipProps,
    XAxis,
    YAxis,
} from 'recharts';
import { GuestUserStats } from '../../../../types/dashboard';
import {
    GuestUserCategoryBreakdown,
    GuestUserCategoryPoint,
    guestUserBreakdownToPoints,
} from '../../guestUserStatsFormat';
import {
    ADMIN_STAT_CHART_MAX_BAR_SIZE,
    getGuestUserBarColor,
} from '../../reportChartConfig';

type GuestUsersReportGraphProps = {
    data?: GuestUserStats;
    breakdown: GuestUserCategoryBreakdown;
};

const GuestUserTooltip = ({
    active,
    payload,
    label,
}: TooltipProps<number, string>) => {
    if (!active || !payload?.length) {
        return null;
    }

    const point = payload[0]?.payload as GuestUserCategoryPoint;

    return (
        <Box
            sx={{
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1,
            }}
        >
            <Typography variant='caption'>{label}</Typography>
            <Typography variant='body2'>Users: {point.count}</Typography>
        </Box>
    );
};

export default function GuestUsersReportGraph({
    data,
    breakdown,
}: GuestUsersReportGraphProps) {
    const theme = useTheme();
    const points = guestUserBreakdownToPoints(data, breakdown);

    if (!points.length) {
        return (
            <Typography sx={{ mt: 4 }} variant='body2'>
                No guest users found.
            </Typography>
        );
    }

    return (
        <Box sx={{ width: '100%', height: 420, mt: 3 }}>
            <ResponsiveContainer width='100%' height='100%'>
                <BarChart
                    data={points}
                    margin={{ left: 30 }}
                    maxBarSize={ADMIN_STAT_CHART_MAX_BAR_SIZE}
                >
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis dataKey='label' />
                    <YAxis allowDecimals={false} />
                    <Tooltip content={<GuestUserTooltip />} />
                    <Legend />
                    <Bar dataKey='count' name='Users'>
                        {points.map((point) => (
                            <Cell
                                key={point.label}
                                fill={getGuestUserBarColor(point.label, theme)}
                            />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </Box>
    );
}
